import {Drawer} from 'components/customer/Product/entity/Drawer';
import {toNumber} from 'components/customer/Product/Drawer/helpers';

// what the fixed face asks for, against one left sharing
const SHALLOW_TOP_SHARE = 2 / 3;
const DEEP_BOTTOM_SHARE = 2;
// caps the two thirds, however tall the cabinet
const SHALLOW_TOP_MAX = 180;
// the bottom of a graduated set, the rest stepping evenly up to it
const GRADUATED_BOTTOM = 2;

export interface DrawerPreset {
    label: string;
    // one entry per drawer; null leaves that drawer on auto
    heights: (number | null)[];
    shape: number[];
    isCustom?: boolean;
}

const round = (value: number) => Math.round(value * 10) / 10;

const sum = (values: number[]) =>
    values.reduce((total, value) => total + value, 0);

// solves share * each + (count - 1) * each = space
const relativeHeight = (share: number, count: number, space: number) =>
    (space * share) / (share + count - 1);

// Presets deal in whole millimetres. Only the fixed face is ours to write, so
// it carries the rounding and the ones sharing come out whole.
const wholePin = (ideal: number, count: number, space: number) => {
    const sharing = count - 1;
    const each = Math.round((space - ideal) / sharing);
    const pinned = space - each * sharing;

    return pinned > 0 ? round(pinned) : round(ideal);
};

const pinOne = (
    label: string,
    height: number,
    atTop: boolean,
    count: number,
    space: number
): DrawerPreset => {
    const sharing = count - 1;
    const autos = new Array<null>(sharing).fill(null);
    const each = round((space - height) / sharing);
    const shares = new Array<number>(sharing).fill(each);

    // the last one sharing takes the remainder, as it does for real
    shares[sharing - 1] = round(space - height - each * (sharing - 1));

    return {
        label,
        heights: atTop ? [height, ...autos] : [...autos, height],
        shape: atTop ? [height, ...shares] : [...shares, height],
    };
};

// whole steps, the last taking what the rounding of the others leaves
const stepped = (count: number, space: number): number[] => {
    const weights = Array.from(
        {length: count},
        (item, index) => 1 + (index * (GRADUATED_BOTTOM - 1)) / (count - 1)
    );
    const total = sum(weights);
    const steps = weights
        .slice(0, -1)
        .map((weight) => Math.round((space * weight) / total));

    return [...steps, round(space - sum(steps))];
};

export const buildPresets = (count: number, space: number): DrawerPreset[] => {
    // a lone face takes the whole panel, so there is no shape to start from
    if (count < 2) return [];

    const presets: DrawerPreset[] = [
        {
            label: 'Equal',
            heights: new Array<null>(count).fill(null),
            shape: new Array<number>(count).fill(1),
        },
    ];

    if (!(space > 0)) return presets;

    presets.push(
        pinOne(
            'Shallow top',
            Math.min(
                SHALLOW_TOP_MAX,
                wholePin(
                    relativeHeight(SHALLOW_TOP_SHARE, count, space),
                    count,
                    space
                )
            ),
            true,
            count,
            space
        )
    );

    // with two faces Graduated and Deep bottom are the same layout
    if (count > 2) {
        const graduated = stepped(count, space);

        presets.push({
            label: 'Graduated',
            heights: [...graduated.slice(0, -1), null],
            shape: graduated,
        });
    }

    presets.push(
        pinOne(
            'Deep bottom',
            wholePin(
                relativeHeight(DEEP_BOTTOM_SHARE, count, space),
                count,
                space
            ),
            false,
            count,
            space
        )
    );

    return presets;
};

export const drawerShape = (
    drawers: Drawer[],
    faceHeightFieldName: string,
    styleFieldName: string
): (number | null)[] =>
    drawers.map((drawer) =>
        toNumber(drawer[String(styleFieldName)]) === 1
            ? null
            : round(toNumber(drawer[String(faceHeightFieldName)]))
    );

export const shapesMatch = (
    left: (number | null)[],
    right: (number | null)[]
) =>
    left.length === right.length &&
    left.every((value, index) => value === right[Number(index)]);

export const withCustom = (
    presets: DrawerPreset[],
    current: (number | null)[],
    effective: number[]
): DrawerPreset[] =>
    // nothing to start from, so nothing to have departed from either
    presets.length === 0 ||
    presets.some((preset) => shapesMatch(preset.heights, current))
        ? presets
        : [
              ...presets,
              {
                  label: 'Custom',
                  heights: current,
                  shape: effective,
                  isCustom: true,
              },
          ];

// The same shape from a flat list of heights, for a product whose faces are
// not held on drawers. Rounded the same way, so the two can be compared.
export const faceShape = (
    heights: number[],
    auto: boolean[]
): (number | null)[] =>
    heights.map((height, index) =>
        auto[Number(index)] ? null : round(height)
    );
